/**
 * System Entity Definition Templates — Issue #1654
 *
 * Platform-scoped seed for the four core system entities (loan, borrower,
 * application, borrowing_entity). Per-tenant `EntityDefinition` rows fork
 * from these via `templateId` per the #1522 platform-defaults pattern.
 *
 * Used by:
 *   - 32-entity-framework-service seed (creates the template rows)
 *   - 32-entity-framework-service `SystemEntitiesService.bootstrapForBusiness`
 *     (forks per-tenant rows on tenant signup + backfill)
 *
 * `primaryField` and `secondaryField` reference typed columns owned by the
 * source-of-truth services (Case, Consumer, CaseSubmission, BorrowingEntity)
 * — not field_definition rows in entity-framework. The custom-fields surface
 * adds new `FieldDefinition` rows on top.
 */

import { SystemEntityType } from '../enums/system-entity-types';

export interface SystemEntityTemplate {
  internalName: SystemEntityType;
  pluralName: string;
  label: string;
  pluralLabel: string;
  description: string;
  icon: string;
  color: string;
  primaryField: string;
  secondaryField?: string;
}

export const SYSTEM_ENTITY_TEMPLATES: readonly SystemEntityTemplate[] = [
  {
    internalName: SystemEntityType.LOAN,
    pluralName: 'loans',
    label: 'Loan',
    pluralLabel: 'Loans',
    description: 'Loan files (cases) tracked through origination, processing, and servicing.',
    icon: 'AccountBalance',
    color: '#2DD4BF',
    primaryField: 'case_number',
    secondaryField: 'borrower_name',
  },
  {
    internalName: SystemEntityType.BORROWER,
    pluralName: 'borrowers',
    label: 'Borrower',
    pluralLabel: 'Borrowers',
    description: 'Individual or entity consumers applying for or holding loans.',
    icon: 'Person',
    color: '#60A5FA',
    primaryField: 'full_name',
    secondaryField: 'email',
  },
  {
    internalName: SystemEntityType.APPLICATION,
    pluralName: 'applications',
    label: 'Application',
    pluralLabel: 'Applications',
    description: 'Loan application submissions awaiting underwriting.',
    icon: 'Description',
    color: '#A78BFA',
    primaryField: 'submission_id',
    secondaryField: 'submitted_at',
  },
  {
    internalName: SystemEntityType.BORROWING_ENTITY,
    pluralName: 'borrowing_entities',
    label: 'Borrowing Entity',
    pluralLabel: 'Borrowing Entities',
    description: 'Legal entities (LLCs, trusts, partnerships) that hold loans.',
    icon: 'Business',
    color: '#F59E0B',
    primaryField: 'legal_name',
    secondaryField: 'entity_type',
  },
] as const;

export const SYSTEM_ENTITY_TEMPLATES_BY_NAME: Readonly<Record<SystemEntityType, SystemEntityTemplate>> = Object.freeze(
  Object.fromEntries(SYSTEM_ENTITY_TEMPLATES.map((t) => [t.internalName, t])) as Record<
    SystemEntityType,
    SystemEntityTemplate
  >,
);

// ============================================================================
// SYSTEM FIELD DEFINITION TEMPLATES (Issue #1654 follow-up)
//
// Platform-scoped baseline FieldDefinitions per system entity. Forked into
// per-tenant `FieldDefinition` rows by `BootstrapSystemEntities` (and by the
// 20260515 data migration for existing tenants).
//
// NAMING CONVENTION — IMPORTANT
// -----------------------------
// `name` MUST equal the TS / Prisma key that the source-of-truth service
// passes into `Object.keys(data)` when it calls `notifyRecordChange`. The
// close outbound-sync activity does `field.name === changedFieldName` to
// decide whether a mapping is applicable. Misaligned names = silently no-op
// outbound mappings.
//
// For borrower we therefore use the keys from `UpdateConsumerInput` in
// `29-case-management-service/src/modules/consumers/consumers.service.ts`:
// `name | phone | status | consumerType | profile | metadata | trustScore | tier`.
//
// Loan / Application / Borrowing Entity templates ship the minimal core
// fields the outbound paths need today. Extend per service when their
// `NotifyRecordChange` emitters land.
// ============================================================================

import type { FieldType } from './domains/entities';

export interface SystemFieldTemplate {
  /** Entity template's stable internal name (loan / borrower / …). */
  entityType: SystemEntityType;
  /** MUST match the TS key the source service emits in `changedFieldNames`. */
  name: string;
  label: string;
  description?: string;
  fieldType: FieldType;
  required?: boolean;
  unique?: boolean;
  defaultValue?: unknown;
  /** SELECT / MULTI_SELECT options: `[{ value, label, color? }]`. */
  options?: Array<{ value: string; label: string; color?: string }>;
  placeholder?: string;
  helpText?: string;
  section?: string;
  displayOrder: number;
  showInList?: boolean;
  showInCreate?: boolean;
  showInDetail?: boolean;
  showInSearch?: boolean;
}

export const SYSTEM_FIELD_TEMPLATES: readonly SystemFieldTemplate[] = [
  // ── Borrower (keys from UpdateConsumerInput) ─────────────────────────────
  {
    entityType: SystemEntityType.BORROWER,
    name: 'name',
    label: 'Full Name',
    description: 'Display name shown in lists and headings.',
    fieldType: 'TEXT' as FieldType,
    required: true,
    section: 'Identity',
    displayOrder: 1,
    showInList: true,
    showInCreate: true,
    showInDetail: true,
    showInSearch: true,
  },
  {
    // NOTE: `email` is exposed for INBOUND mappings (Close contact's email
    // can sync into fiops Consumer.email via close-field-mapping inbound
    // apply). It is dead-end for OUTBOUND today because
    // `UpdateConsumerInput` in `29-case-management-service/src/modules/
    // consumers/consumers.service.ts:80` has no `email` key — so
    // `consumers.update` tRPC never emits `email` in
    // `NotifyRecordChange.changedFieldNames`. When a future emitter starts
    // tracking email mutations, this template makes the OUTBOUND target
    // available immediately.
    entityType: SystemEntityType.BORROWER,
    name: 'email',
    label: 'Email',
    description: 'Primary contact email. Unique per tenant.',
    fieldType: 'EMAIL' as FieldType,
    required: true,
    unique: true,
    section: 'Identity',
    displayOrder: 2,
    showInList: true,
    showInCreate: true,
    showInDetail: true,
    showInSearch: true,
  },
  {
    entityType: SystemEntityType.BORROWER,
    name: 'phone',
    label: 'Phone',
    description: 'Primary phone number.',
    fieldType: 'PHONE' as FieldType,
    section: 'Identity',
    displayOrder: 3,
    showInList: true,
    showInCreate: true,
    showInDetail: true,
    showInSearch: false,
  },
  {
    entityType: SystemEntityType.BORROWER,
    name: 'status',
    label: 'Status',
    description: 'Lifecycle status of the borrower record.',
    fieldType: 'SELECT' as FieldType,
    required: true,
    options: [
      { value: 'pending', label: 'Pending', color: '#F59E0B' },
      { value: 'active', label: 'Active', color: '#10B981' },
      { value: 'inactive', label: 'Inactive', color: '#9CA3AF' },
      { value: 'suspended', label: 'Suspended', color: '#EF4444' },
    ],
    section: 'Status',
    displayOrder: 4,
    showInList: true,
    showInCreate: false,
    showInDetail: true,
    showInSearch: true,
  },
  {
    entityType: SystemEntityType.BORROWER,
    name: 'consumerType',
    label: 'Type',
    description: 'Whether this consumer is an end consumer or a corporate principal.',
    fieldType: 'SELECT' as FieldType,
    options: [
      { value: 'END_CONSUMER', label: 'End Consumer' },
      { value: 'CORPORATE_PRINCIPAL', label: 'Corporate Principal' },
    ],
    section: 'Status',
    displayOrder: 5,
    showInList: false,
    showInCreate: true,
    showInDetail: true,
    showInSearch: false,
  },

  // ── Borrowing Entity (legal entities; BorrowingEntity service columns) ──
  {
    entityType: SystemEntityType.BORROWING_ENTITY,
    name: 'legalName',
    label: 'Legal Name',
    description: 'Legal name on filings / contracts.',
    fieldType: 'TEXT' as FieldType,
    required: true,
    section: 'Identity',
    displayOrder: 1,
    showInList: true,
    showInCreate: true,
    showInDetail: true,
    showInSearch: true,
  },
  {
    entityType: SystemEntityType.BORROWING_ENTITY,
    name: 'entityType',
    label: 'Entity Type',
    description: 'Legal form (LLC / Trust / Partnership / Corporation).',
    fieldType: 'SELECT' as FieldType,
    required: true,
    options: [
      { value: 'llc', label: 'LLC' },
      { value: 'trust', label: 'Trust' },
      { value: 'partnership', label: 'Partnership' },
      { value: 'corporation', label: 'Corporation' },
      { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
    ],
    section: 'Identity',
    displayOrder: 2,
    showInList: true,
    showInCreate: true,
    showInDetail: true,
    showInSearch: true,
  },
  {
    entityType: SystemEntityType.BORROWING_ENTITY,
    name: 'ein',
    label: 'EIN',
    description: 'Employer Identification Number.',
    fieldType: 'TEXT' as FieldType,
    unique: true,
    section: 'Identity',
    displayOrder: 3,
    showInList: false,
    showInCreate: true,
    showInDetail: true,
    showInSearch: true,
  },

  // ── Loan (case-mgmt Case columns) ───────────────────────────────────────
  {
    entityType: SystemEntityType.LOAN,
    name: 'caseNumber',
    label: 'Loan Number',
    description: 'Unique loan / case number for tracking and references.',
    fieldType: 'TEXT' as FieldType,
    required: true,
    unique: true,
    section: 'Identity',
    displayOrder: 1,
    showInList: true,
    showInCreate: false,
    showInDetail: true,
    showInSearch: true,
  },
  {
    entityType: SystemEntityType.LOAN,
    name: 'status',
    label: 'Status',
    description: 'Current pipeline status of the loan.',
    fieldType: 'SELECT' as FieldType,
    required: true,
    options: [
      { value: 'open', label: 'Open' },
      { value: 'in_progress', label: 'In Progress' },
      { value: 'on_hold', label: 'On Hold' },
      { value: 'closed', label: 'Closed' },
      { value: 'cancelled', label: 'Cancelled' },
    ],
    section: 'Status',
    displayOrder: 2,
    showInList: true,
    showInCreate: false,
    showInDetail: true,
    showInSearch: true,
  },
  {
    entityType: SystemEntityType.LOAN,
    name: 'priority',
    label: 'Priority',
    description: 'Workflow priority of the loan.',
    fieldType: 'SELECT' as FieldType,
    options: [
      { value: 'low', label: 'Low' },
      { value: 'normal', label: 'Normal' },
      { value: 'high', label: 'High' },
      { value: 'urgent', label: 'Urgent' },
    ],
    section: 'Status',
    displayOrder: 3,
    showInList: true,
    showInCreate: true,
    showInDetail: true,
    showInSearch: false,
  },
  {
    entityType: SystemEntityType.LOAN,
    name: 'requestedAmount',
    label: 'Requested Amount',
    description: 'Loan amount requested by the borrower.',
    fieldType: 'CURRENCY' as FieldType,
    section: 'Terms',
    displayOrder: 4,
    showInList: true,
    showInCreate: true,
    showInDetail: true,
    showInSearch: false,
  },

  // ── Application (CaseSubmission columns) ────────────────────────────────
  {
    entityType: SystemEntityType.APPLICATION,
    name: 'submissionId',
    label: 'Application ID',
    description: 'Unique submission identifier.',
    fieldType: 'TEXT' as FieldType,
    required: true,
    unique: true,
    section: 'Identity',
    displayOrder: 1,
    showInList: true,
    showInCreate: false,
    showInDetail: true,
    showInSearch: true,
  },
  {
    entityType: SystemEntityType.APPLICATION,
    name: 'status',
    label: 'Status',
    description: 'Current application status.',
    fieldType: 'SELECT' as FieldType,
    required: true,
    options: [
      { value: 'submitted', label: 'Submitted' },
      { value: 'in_review', label: 'In Review' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'withdrawn', label: 'Withdrawn' },
    ],
    section: 'Status',
    displayOrder: 2,
    showInList: true,
    showInCreate: false,
    showInDetail: true,
    showInSearch: true,
  },
  {
    entityType: SystemEntityType.APPLICATION,
    name: 'submittedAt',
    label: 'Submitted At',
    description: 'When the application was submitted.',
    fieldType: 'DATETIME' as FieldType,
    section: 'Status',
    displayOrder: 3,
    showInList: true,
    showInCreate: false,
    showInDetail: true,
    showInSearch: false,
  },
] as const;

/** Convenience: field templates grouped by entity internal name. */
export const SYSTEM_FIELD_TEMPLATES_BY_ENTITY: Readonly<Record<SystemEntityType, readonly SystemFieldTemplate[]>> =
  Object.freeze(
    Object.fromEntries(
      SYSTEM_ENTITY_TEMPLATES.map((t) => [
        t.internalName,
        SYSTEM_FIELD_TEMPLATES.filter((f) => f.entityType === t.internalName),
      ]),
    ) as unknown as Record<SystemEntityType, readonly SystemFieldTemplate[]>,
  );
